import type { InvoiceDirection, InvoiceStats } from '@tandemone/types'
import { CircleDollarSign, Clock, Receipt, TrendingUp } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'

import { formatMoney } from './utils'

/** labels flip with direction: 'sent' is my receivables (money owed to me),
 * 'received' is my payables (money I owe). same four figures either way.
 */
const LABELS: Record<
  InvoiceDirection,
  { revenue: string; outstanding: string; count: string }
> = {
  sent: {
    revenue: 'Total revenue',
    outstanding: 'Outstanding',
    count: 'Invoices'
  },
  received: { revenue: 'Total paid', outstanding: 'You owe', count: 'Bills' }
}

export function InvoiceStatCards({
  stats,
  isLoading,
  direction
}: {
  stats: InvoiceStats | undefined
  isLoading: boolean
  direction: InvoiceDirection
}) {
  const labels = LABELS[direction]
  return (
    <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
      <StatCard
        icon={<TrendingUp className='size-4' />}
        label={labels.revenue}
        value={stats ? formatMoney(stats.totalRevenue, 'usd') : undefined}
        isLoading={isLoading}
      />
      <StatCard
        icon={<Clock className='size-4' />}
        label={labels.outstanding}
        value={stats ? formatMoney(stats.outstandingAmount, 'usd') : undefined}
        isLoading={isLoading}
      />
      <StatCard
        icon={<Receipt className='size-4' />}
        label={labels.count}
        value={stats ? String(stats.invoiceCount) : undefined}
        isLoading={isLoading}
      />
      <StatCard
        icon={<CircleDollarSign className='size-4' />}
        label='Paid rate'
        value={stats ? `${stats.paidRate}%` : undefined}
        isLoading={isLoading}
      />
    </div>
  )
}

function StatCard({
  icon,
  label,
  value,
  isLoading
}: {
  icon: React.ReactNode
  label: string
  value: string | undefined
  isLoading: boolean
}) {
  return (
    <div className='flex flex-col gap-2 rounded-xl border border-border bg-card p-3 sm:gap-3 sm:p-4'>
      <span className='flex size-7 items-center justify-center rounded-lg bg-cream text-accent sm:size-8 dark:text-accent-mid'>
        {icon}
      </span>
      <div className='flex flex-col gap-1'>
        <span className='text-[12px] font-medium text-ink-muted'>{label}</span>
        {isLoading || value === undefined ? (
          <Skeleton className='h-6 w-20 sm:h-7 sm:w-24' />
        ) : (
          <span className='text-xl font-semibold text-ink sm:text-2xl'>
            {value}
          </span>
        )}
      </div>
    </div>
  )
}
